import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import type { ApiResponse, HttpClientConfig, RequestConfig, HttpError, ErrorCode } from "./types";

/**
 * HTTP 客户端类
 * 封装 axios 实例，提供统一的请求方法和拦截器
 */
export class HttpClient {
  private instance: AxiosInstance;
  private config: HttpClientConfig;

  constructor(config: HttpClientConfig = {}) {
    this.config = config;
    this.instance = axios.create({
      baseURL: config.baseURL || "",
      timeout: config.timeout || 10000,
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
    });

    this.setupInterceptors();
  }

  /**
   * 设置拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 添加 token
        const token = this.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 执行自定义请求拦截器
        this.config.requestInterceptors?.forEach((interceptor) => {
          if (interceptor.onFulfilled) {
            config = interceptor.onFulfilled(config) as InternalAxiosRequestConfig;
          }
        });

        return config;
      },
      (error: unknown) => {
        // 执行自定义错误处理
        this.config.requestInterceptors?.forEach((interceptor) => {
          if (interceptor.onRejected) {
            interceptor.onRejected(error);
          }
        });
        return Promise.reject(error);
      },
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 执行自定义响应拦截器
        this.config.responseInterceptors?.forEach((interceptor) => {
          if (interceptor.onFulfilled) {
            response = interceptor.onFulfilled(response) as AxiosResponse;
          }
        });
        return response;
      },
      (error: unknown) => {
        const httpError = this.handleError(error);

        // 执行自定义错误处理
        this.config.responseInterceptors?.forEach((interceptor) => {
          if (interceptor.onRejected) {
            interceptor.onRejected(error);
          }
        });

        return Promise.reject(httpError);
      },
    );
  }

  /**
   * 获取 token
   */
  private getToken(): string | null {
    return localStorage.getItem("token");
  }

  /**
   * 处理错误
   */
  private handleError(error: unknown): HttpError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiResponse>;

      if (axiosError.response) {
        const { status, data } = axiosError.response;
        return {
          code: status as ErrorCode,
          message: data?.message || this.getErrorMessage(status),
          data: data?.data,
        };
      }

      if (axiosError.code === "ECONNABORTED") {
        return {
          code: -2 as ErrorCode,
          message: "Request timeout",
        };
      }

      return {
        code: -1 as ErrorCode,
        message: "Network error",
      };
    }

    return {
      code: -999 as ErrorCode,
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }

  /**
   * 根据状态码获取错误信息
   */
  private getErrorMessage(status: number): string {
    const messages: Record<number, string> = {
      400: "Bad request",
      401: "Unauthorized",
      403: "Forbidden",
      404: "Not found",
      500: "Internal server error",
      502: "Bad gateway",
      503: "Service unavailable",
    };
    return messages[status] || `HTTP Error: ${status}`;
  }

  /**
   * 统一处理响应: code=0 且 status=200 返回 data，否则 reject 完整响应
   */
  private handleResponse<T>(response: AxiosResponse<ApiResponse<T>>): T {
    const { status } = response;
    const result = response.data;

    if (status === 200 && result.code === 0) {
      return result.data;
    }

    return Promise.reject(result) as never;
  }

  /**
   * GET 请求
   */
  async get<T>(url: string, params?: Record<string, unknown>, config?: RequestConfig): Promise<T> {
    const response = await this.instance.get<ApiResponse<T>>(url, { ...config, params });
    return this.handleResponse<T>(response);
  }

  /**
   * POST 请求
   */
  async post<T, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<T> {
    const response = await this.instance.post<ApiResponse<T>>(url, data, config);
    return this.handleResponse<T>(response);
  }

  /**
   * PUT 请求
   */
  async put<T, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<T> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config);
    return this.handleResponse<T>(response);
  }

  /**
   * DELETE 请求
   */
  async delete<T>(url: string, config?: RequestConfig): Promise<T> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config);
    return this.handleResponse<T>(response);
  }

  /**
   * PATCH 请求
   */
  async patch<T, D = unknown>(
    url: string,
    data?: D,
    config?: RequestConfig<D>,
  ): Promise<T> {
    const response = await this.instance.patch<ApiResponse<T>>(url, data, config);
    return this.handleResponse<T>(response);
  }

  /**
   * 上传文件
   */
  async upload<T>(
    url: string,
    file: File,
    fieldName = "file",
    config?: RequestConfig,
  ): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await this.instance.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
    });
    return this.handleResponse<T>(response);
  }

  /**
   * 下载文件
   */
  async download(url: string, filename: string, config?: RequestConfig): Promise<void> {
    const response = await this.instance.get(url, {
      ...config,
      responseType: "blob",
    });

    const blob = new Blob([response.data]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}
